import { USER_IDLE_THRESHOLD_MS } from './constants';

// ---------------------------------------------------------------------------
// Module-level constants (hoisted — no render-time allocation)
// ---------------------------------------------------------------------------

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  // `scroll` only fires on the document for page-level scrolls; inner-panel
  // scrolling with a stationary pointer emits `wheel` on the document instead.
  'scroll',
  'wheel',
  'click',
  'touchstart',
] as const;

const ACTIVITY_THROTTLE_MS = 1000;

// ---------------------------------------------------------------------------
// Module-level private state
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();
let asleep = false;
let lastActivityAt = Date.now();
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let refCount = 0;

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function notify(): void {
  for (const l of listeners) {
    l();
  }
}

function setAsleep(next: boolean): void {
  if (next === asleep) {
    return;
  }
  asleep = next;
  notify();
}

function armIdleTimer(): void {
  if (idleTimer) {
    clearTimeout(idleTimer);
  }
  idleTimer = setTimeout(onIdleTimeout, USER_IDLE_THRESHOLD_MS);
}

function onIdleTimeout(): void {
  idleTimer = null;
  const elapsed = Date.now() - lastActivityAt;
  if (elapsed >= USER_IDLE_THRESHOLD_MS) {
    setAsleep(true);
  } else {
    // Activity landed after arming — re-arm for the remainder.
    idleTimer = setTimeout(onIdleTimeout, USER_IDLE_THRESHOLD_MS - elapsed);
  }
}

function handleActivity(): void {
  const now = Date.now();
  if (asleep) {
    lastActivityAt = now;
    setAsleep(false);
    armIdleTimer();
    return;
  }
  if (now - lastActivityAt < ACTIVITY_THROTTLE_MS) {
    return;
  }
  lastActivityAt = now;
  armIdleTimer();
}

function startMonitoring(): void {
  lastActivityAt = Date.now();
  setAsleep(false);
  for (const e of ACTIVITY_EVENTS) {
    // Capture phase so a control that calls stopPropagation() cannot hide user
    // input from us — the document listener runs before the event bubbles.
    document.addEventListener(e, handleActivity, {
      passive: true,
      capture: true,
    });
  }
  armIdleTimer();
}

function stopMonitoring(): void {
  for (const e of ACTIVITY_EVENTS) {
    // Options must match addEventListener's `capture: true` for removal to work.
    document.removeEventListener(e, handleActivity, { capture: true });
  }
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function subscribeActivityMonitor(listener: () => void): () => void {
  listeners.add(listener);
  refCount += 1;
  if (refCount === 1) {
    startMonitoring();
  }
  return () => {
    if (!listeners.has(listener)) {
      return;
    }
    listeners.delete(listener);
    refCount -= 1;
    if (refCount === 0) {
      stopMonitoring();
    }
  };
}

export function getActivityMonitorSnapshot(): boolean {
  return asleep;
}

/**
 * Imperative sleep read that also accounts for elapsed idle time. Unlike the
 * `asleep` flag — which only flips when `idleTimer` fires — this is robust to
 * background-tab timer throttling: a hidden tab throttles `setTimeout`, so the
 * idle timer can fire late (or not until the tab is foregrounded), leaving
 * `asleep` false for a user who has actually been idle past the threshold. A
 * caller that reads at an arbitrary moment (e.g. NewVersionBanner on
 * visibilitychange) would otherwise see "awake" and skip the auto-refresh.
 *
 * This is deliberately NOT the `useSyncExternalStore` snapshot
 * (`getActivityMonitorSnapshot`): it is time-derived and returns a fresh value
 * on each call, which would violate snapshot caching and loop React. Read it
 * imperatively only.
 */
export function isAsleepByElapsedIdle(): boolean {
  // The no-subscriber guard runs FIRST. Once the last subscriber unsubscribes,
  // monitoring is torn down but the `asleep` flag is not reset (the last
  // subscriber may have left while asleep — `stopMonitoring` never flips it).
  // Both `asleep` and `lastActivityAt` are therefore stale leftovers with no
  // subscribers; treat that state as awake so imperative callers can't act on a
  // sleep reading from a monitor that is no longer tracking.
  if (refCount === 0) {
    return false;
  }
  if (asleep) {
    return true;
  }
  return Date.now() - lastActivityAt >= USER_IDLE_THRESHOLD_MS;
}

export function getActivityMonitorServerSnapshot(): boolean {
  return false;
}

export function __resetActivityMonitorForTesting(): void {
  // Tear down any installed document handlers and the idle timer before
  // discarding state, so later activity can't restart timers without
  // subscribers.
  stopMonitoring();
  listeners.clear();
  refCount = 0;
  asleep = false;
  lastActivityAt = Date.now();
}
